import { useRolodexEntries, useRolodexSignedURLs } from 'features/rolodex'
import { useMemo } from 'react'
import {
  GeckoFileCategory,
  GeckoReport,
  GeckoSite,
  GeckoVisualInspection,
  RolodexEntryType,
} from 'types'

export interface VisualInspectionImage {
  id: number
  report: GeckoReport
  signedUrl: string
  description: string
}

/**
 * A hook that fetches and processes visual inspection images
 * @param selectedVisualInspection
 * @returns {
 *  images: VisualInspectionImage[]
 *  isLoading: boolean
 * }
 */
export const useVisualInspectionImages = (
  selectedVisualInspection: GeckoVisualInspection | undefined,
  site: GeckoSite | undefined
): {
  images: VisualInspectionImage[]
  isLoading: boolean
} => {
  const { reports, isLoading } = useRolodexEntries(
    selectedVisualInspection && site
      ? {
          type: RolodexEntryType.REPORT,
          tags: { site: site.id },
          filters: [
            {
              type: RolodexEntryType.REPORT,
              field: 'data',
              key: 'visual_inspections',
              op: 'contains',
              value: [selectedVisualInspection.id],
            },
          ],
        }
      : undefined
  )

  // Filter for image reports only
  const imageReports = useMemo(() => {
    if (!reports) return []
    return reports.filter(
      (report: GeckoReport) =>
        report.data.file_category === GeckoFileCategory.IMAGE
    )
  }, [reports])

  // Get signed URLs for the images
  const photoFilePaths = useMemo(() => {
    return imageReports.map((report) => ({
      entry_id: report.id,
      path: report.id,
    }))
  }, [imageReports])

  const { results: photoSignedUrls } = useRolodexSignedURLs(photoFilePaths)

  // Combine reports with their signed URLs and descriptions
  const imagesWithUrls = useMemo(() => {
    return imageReports.map((report, index) => ({
      id: index + 1,
      report,
      signedUrl: photoSignedUrls[index]?.signed_url,
      description:
        report.data.file_caption ||
        report.data.visibleName ||
        `Image ${index + 1}`,
    }))
  }, [imageReports, photoSignedUrls])

  return {
    images: imagesWithUrls,
    isLoading,
  }
}
